import React, { useEffect, useState } from "react";
import { useAuth } from "../components/context/AuthContext";
import { useBookings } from "../components/context/BookingContext";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Popup from "../components/home/popup";
import SkeletonLoader from "../components/common/SkeletonLoader";

export default function UserDashboard() {
  const { user, logout, isLoading } = useAuth();
  const { bookings, rooms, activeCoupons, isLoadingBookings, refreshBookings, refreshActiveCoupons, cancelBooking } = useBookings();
  const navigate = useNavigate();
  
  const [countdownText, setCountdownText] = useState("");
  const [activeTab, setActiveTab] = useState("bookings");
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showBookingPopup, setShowBookingPopup] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState("");

  // Redirect if not authenticated as user
  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  // Compute metrics
  const totalBookings = bookings.length;
  const pendingBookings = bookings.filter((b) => b.status === "Pending" || b.status === "Syncing...").length;
  const confirmedBookings = bookings.filter((b) => b.status === "Confirmed").length;
  const completedBookings = bookings.filter((b) => b.status === "Completed").length;

  // Active check-in countdown timer
  useEffect(() => {
    if (bookings.length === 0) return;
    const upcoming = bookings.find((b) => b.status === "Confirmed" || b.status === "Pending");
    if (!upcoming || !upcoming.checkInDate) return;

    const targetDate = new Date(upcoming.checkInDate).getTime();
    
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate - now;

      if (distance < 0) {
        setCountdownText("Welcome to HariKunj Resort!");
        clearInterval(timer);
      } else {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        setCountdownText(`Your royal stay begins in: ${days}d ${hours}h ${minutes}m`);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [bookings]);

  const handleCancelClick = async (booking) => {
    const targetId = booking.bookingId || booking.id;
    if (window.confirm(`Are you sure you want to cancel your reservation for ${booking.roomType} (ID: ${targetId})?`)) {
      const res = await cancelBooking(targetId);
      if (res.success) {
        alert(res.message || "Your reservation has been cancelled.");
      } else {
        alert(res.error || "Failed to cancel reservation.");
      }
    }
  };

  const handleBookNowClick = (roomType) => {
    setSelectedRoomType(roomType);
    setShowBookingPopup(true);
  };

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return "G";
    const parts = name.split(" ");
    return parts.map((p) => p[0]).join("").substring(0, 2).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="container py-5">
        <SkeletonLoader type="card" count={3} />
      </div>
    );
  }

  if (!user) return null;

  return (
    <>
      <Helmet>
        <title>Guest Suite Dashboard – HariKunj Resort Jodhpur</title>
        <meta name="description" content="View and manage your royal heritage room bookings and check-in status at HariKunj Resort, Jodhpur." />
      </Helmet>

      <div className="hk-dashboard-wrapper">
        
        {/* Toggle Button for Mobile Sidebar */}
        <button 
          type="button" 
          className="hk-mobile-toggle-btn d-lg-none"
          onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
          aria-label="Toggle Navigation Sidebar"
        >
          <i className={`fa ${isMobileSidebarOpen ? "fa-times" : "fa-bars"}`} />
        </button>

        {/* Sidebar Navigation */}
        <aside className={`hk-sidebar ${isMobileSidebarOpen ? "hk-sidebar--open" : ""}`}>
          <div className="hk-sidebar-brand">
            <span className="logo-main">HariKunj</span>
            <span className="logo-sub">Resort &bull; Guest Suite</span>
          </div>

          <div className="hk-sidebar-profile">
            <div className="profile-avatar">{getInitials(user.fullName)}</div>
            <div className="profile-name">{user.fullName}</div>
            <div className="profile-email">{user.email}</div>
          </div>

          <nav className="hk-sidebar-nav">
            <button 
              onClick={() => { setActiveTab("bookings"); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "bookings" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-calendar-check me-3" /> My Bookings
            </button>
            <button 
              onClick={() => { setActiveTab("book"); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "book" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-bed me-3" /> Book a Stay
            </button>
            <button 
              onClick={() => { setActiveTab("support"); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "support" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-headset me-3" /> Resort Support
            </button>
            <button 
              onClick={() => { setActiveTab("offers"); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "offers" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-tags me-3" /> Offers & Coupons ({activeCoupons ? activeCoupons.length : 0})
            </button>
          </nav>

          <div className="hk-sidebar-footer">
            <button type="button" className="btn hk-btn-logout w-100" onClick={logout}>
              <i className="fa fa-sign-out-alt me-2" /> Log Out
            </button>
          </div>
        </aside>

        {/* Mobile Sidebar Overlay Backdrop */}
        {isMobileSidebarOpen && (
          <div 
            className="hk-sidebar-backdrop d-lg-none"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Main Content Area */}
        <main className="hk-main-content">
          <div className="container-fluid py-4 px-lg-4">
            
            {/* Countdown banner if any */}
            {countdownText && (
              <div className="hk-countdown-banner mb-4 p-3 text-center rounded-3 shadow-sm border border-warning animate-pulse">
                <i className="fa fa-clock me-2 text-warning" />
                <strong>{countdownText}</strong>
              </div>
            )}

            {/* TAB CONTENT: BOOKINGS */}
            {activeTab === "bookings" && (
              <>
                <div className="hk-welcome-panel mb-4 p-4 rounded-4 shadow-sm d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                  <div>
                    <span className="hk-gold-tag">Guest Suite Portal</span>
                    <h2 className="hk-welcome-title mt-2 mb-0">Welcome Back, {user.fullName}!</h2>
                    <p className="hk-welcome-sub mb-0 mt-1">Manage your heritage bookings and explore your upcoming Jodhpur stay details.</p>
                  </div>
                  <button type="button" className="btn btn-outline-light rounded-pill px-4" onClick={() => { refreshBookings(); refreshActiveCoupons(); }}>
                    <i className={`fa fa-sync-alt me-2 ${isLoadingBookings ? "fa-spin" : ""}`} /> Refresh
                  </button>
                </div>

                {/* Metrics Grid */}
                <div className="row g-3 mb-4">
                  <div className="col-6 col-md-3">
                    <div className="hk-metric-card">
                      <i className="fa fa-calendar-check fa-2x mb-2 text-primary" />
                      <h3>{totalBookings}</h3>
                      <p>Total Bookings</p>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="hk-metric-card">
                      <i className="fa fa-star fa-2x mb-2 text-success" />
                      <h3>{confirmedBookings}</h3>
                      <p>Confirmed Stays</p>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="hk-metric-card">
                      <i className="fa fa-hourglass-half fa-2x mb-2 text-warning" />
                      <h3>{pendingBookings}</h3>
                      <p>Pending Approval</p>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="hk-metric-card">
                      <i className="fa fa-history fa-2x mb-2 text-info" />
                      <h3>{completedBookings}</h3>
                      <p>Past Visits</p>
                    </div>
                  </div>
                </div>

                {/* Bookings Section */}
                <div className="hk-bookings-section p-4 rounded-4 shadow-sm bg-white">
                  <h4 className="hk-section-header mb-4">Your Royal Bookings</h4>
                  
                  {bookings.length === 0 ? (
                    <div className="text-center py-5 hk-empty-state">
                      <i className="fa fa-bed fa-3x mb-3 text-muted" />
                      <h5>No Bookings Logged Yet</h5>
                      <p className="text-muted">You haven't requested any stay reservations yet. Ready to experience Jodhpur royalty?</p>
                      <button type="button" className="btn hk-btn-gold rounded-pill mt-3 px-4 py-2" onClick={() => setActiveTab("book")}>
                        Explore Our Rooms
                      </button>
                    </div>
                  ) : (
                    <div className="row g-3">
                      {bookings.map((booking) => (
                        <div className="col-12 col-xl-6" key={booking.bookingId}>
                          <div className="hk-booking-detail-card">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <h5 className="hk-room-name">{booking.roomType}</h5>
                                <span className="hk-booking-id">ID: {booking.bookingId}</span>
                              </div>
                              <span className={`hk-badge hk-badge-${booking.status.toLowerCase().replace("...", "").trim()}`}>
                                {booking.status}
                              </span>
                            </div>

                            <div className="hk-card-gold-line mb-3" />

                            <div className="row g-2 mb-3">
                              <div className="col-6">
                                <small className="d-block text-muted">CHECK-IN</small>
                                <strong>{booking.checkIn}</strong>
                              </div>
                              <div className="col-6">
                                <small className="d-block text-muted">CHECK-OUT</small>
                                <strong>{booking.checkOut}</strong>
                              </div>
                            </div>

                            <div className="row g-2 mb-3">
                              <div className="col-6">
                                <small className="d-block text-muted">GUESTS</small>
                                <strong>{booking.adults} Adults {booking.children > 0 && `, ${booking.children} Children`}</strong>
                              </div>
                              <div className="col-6">
                                <small className="d-block text-muted">TOTAL COST</small>
                                <strong className="hk-gold-cost">₹{Number(booking.totalCost).toLocaleString("en-IN")}</strong>
                              </div>
                            </div>

                            {(booking.status === "Pending" || booking.status === "Confirmed") && (
                              <button
                                type="button"
                                className="btn btn-outline-danger w-100 rounded-pill py-2"
                                onClick={() => handleCancelClick(booking)}
                              >
                                <i className="fab fa-whatsapp me-2" /> Request Cancellation
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* TAB CONTENT: BOOK A STAY */}
            {activeTab === "book" && (
              <div className="hk-bookings-section p-4 rounded-4 shadow-sm bg-white">
                <h4 className="hk-section-header mb-4">Book Your Royal Heritage Stay</h4>
                <p className="text-muted mb-4">Explore live availability across our handcrafted heritage room options and book instantly.</p>
                
                <div className="row g-4">
                  {(rooms && rooms.length > 0 ? rooms : [
                    { roomId: "room_std001", roomType: "Standard Room", pricePerNight: 3999, status: "Available", availableCount: 10 },
                    { roomId: "room_dlx001", roomType: "Deluxe Room", pricePerNight: 4999, status: "Available", availableCount: 6 },
                    { roomId: "room_sdx001", roomType: "Super Deluxe", pricePerNight: 7999, status: "Available", availableCount: 4 }
                  ]).map((room) => (
                    <div className="col-12 col-md-4" key={room.roomId}>
                      <div className="hk-booking-detail-card h-100 d-flex flex-column justify-content-between">
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="hk-room-name">{room.roomType}</h5>
                            <span className={`hk-badge ${room.status === "Available" ? "hk-badge-confirmed" : "hk-badge-cancelled"}`}>
                              {room.status === "Available" ? "Available" : "Fully Booked"}
                            </span>
                          </div>
                          <div className="hk-gold-cost mb-3">₹{room.pricePerNight} <span className="text-muted fs-6">/ night</span></div>
                          <div className="hk-card-gold-line mb-3" />
                          <p className="small text-muted mb-3">
                            Experience Rajasthani luxury and premium comfort with state-of-the-art heritage styling.
                          </p>
                          <div className="mb-4">
                            <i className="fa fa-info-circle text-warning me-2" />
                            <strong>{room.status === "Available" ? `${room.availableCount || 5} rooms available` : "No rooms remaining"}</strong>
                          </div>
                        </div>

                        <button 
                          type="button" 
                          className="btn hk-btn-gold w-100 rounded-pill py-2"
                          onClick={() => handleBookNowClick(room.roomType)}
                          disabled={room.status !== "Available"}
                        >
                          {room.status === "Available" ? "Book This Room" : "Sold Out"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB CONTENT: SUPPORT */}
            {activeTab === "support" && (
              <div className="hk-bookings-section p-4 rounded-4 shadow-sm bg-white">
                <h4 className="hk-section-header mb-4">HariKunj Personal Concierge Services</h4>
                
                <div className="row g-4">
                  <div className="col-12 col-md-6">
                    <div className="hk-booking-detail-card h-100">
                      <h5 className="hk-room-name mb-3">Concierge Contacts</h5>
                      <div className="hk-card-gold-line mb-3" />
                      <div className="d-flex flex-column gap-3">
                        <div>
                          <i className="fa fa-map-marker-alt text-warning me-3 fa-lg" />
                          <strong>Address:</strong>
                          <p className="text-muted ms-4 mb-0">Gopal Badi, Chopasni, Jodhpur, 342008 Rajasthan</p>
                        </div>
                        <div>
                          <i className="fa fa-phone-alt text-warning me-3 fa-lg" />
                          <strong>Phone Support:</strong>
                          <p className="text-muted ms-4 mb-0">+91 9602588882, +91 9602588883</p>
                        </div>
                        <div>
                          <i className="fa fa-envelope text-warning me-3 fa-lg" />
                          <strong>Email Queries:</strong>
                          <p className="text-muted ms-4 mb-0">reservation@harikunjresort.com</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="hk-booking-detail-card h-100 d-flex flex-column justify-content-between">
                      <div>
                        <h5 className="hk-room-name mb-3">WhatsApp Assistance</h5>
                        <div className="hk-card-gold-line mb-3" />
                        <p className="text-muted mb-4">
                          Need any special room preparation, food preferences, local Jodhpur tours, or pick-and-drop service? Message our guest manager instantly on WhatsApp!
                        </p>
                      </div>
                      <a 
                        href="https://wa.me/919602588882?text=Hello%20HariKunj%20Resort!%20I%20am%20a%20registered%20guest%20and%20need%20assistance."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-success w-100 rounded-pill py-2 text-center"
                      >
                        <i className="fab fa-whatsapp me-2" /> Chat with Guest Manager
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </main>
      </div>

      {showBookingPopup && (
        <Popup
          show={showBookingPopup}
          onClose={() => setShowBookingPopup(false)}
          initialRoomType={selectedRoomType}
        />
      )}

      {/* STYLES: Beautiful Responsive Sidebar Layout & Dark-Blue/Gold Aesthetics */}
      <style>{`
        .hk-dashboard-wrapper {
          display: flex;
          min-height: 100vh;
          width: 100%;
          background: #FAF6F0;
          position: relative;
          color: #0B1D37;
        }

        /* Sidebar container styles */
        .hk-sidebar {
          width: 280px;
          background: linear-gradient(180deg, #0B2447 0%, #050E1A 100%);
          border-right: 1.5px solid rgba(212, 175, 55, 0.35);
          color: #FAF6F0;
          display: flex;
          flex-direction: column;
          padding: 30px 20px;
          flex-shrink: 0;
          z-index: 100;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .hk-sidebar-brand {
          text-align: center;
          padding-bottom: 24px;
          border-bottom: 1.5px solid rgba(212, 175, 55, 0.2);
          display: flex;
          flex-direction: column;
        }
        .hk-sidebar-brand .logo-main {
          font-family: 'Cinzel', serif;
          font-weight: 700;
          color: #FEA116;
          font-size: 1.6rem;
          letter-spacing: 0.05em;
        }
        .hk-sidebar-brand .logo-sub {
          font-family: 'Poppins', sans-serif;
          font-size: 0.72rem;
          font-weight: 600;
          color: #FAF6F0;
          opacity: 0.7;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin-top: 2px;
        }

        .hk-sidebar-profile {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 30px 0 24px;
          border-bottom: 1.5px solid rgba(212, 175, 55, 0.2);
        }
        .profile-avatar {
          width: 68px;
          height: 68px;
          border-radius: 50%;
          background: linear-gradient(135deg, #FEA116 0%, #D4AF37 100%);
          color: #050E1A;
          font-family: 'Cinzel', serif;
          font-size: 1.6rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 16px rgba(254, 161, 22, 0.25);
          border: 2px solid #FAF6F0;
          margin-bottom: 12px;
        }
        .profile-name {
          font-family: 'Cinzel', serif;
          font-weight: 700;
          font-size: 1.05rem;
          color: #FAF6F0;
        }
        .profile-email {
          font-family: 'Poppins', sans-serif;
          font-size: 0.78rem;
          opacity: 0.65;
          margin-top: 2px;
        }

        .hk-sidebar-nav {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 30px 0;
          flex-grow: 1;
        }
        .nav-item {
          background: transparent;
          border: none;
          color: #FAF6F0;
          opacity: 0.75;
          text-align: left;
          padding: 12px 18px;
          border-radius: 12px;
          font-family: 'Poppins', sans-serif;
          font-size: 0.95rem;
          font-weight: 600;
          transition: all 0.25s ease;
          display: flex;
          align-items: center;
          width: 100%;
        }
        .nav-item:hover {
          background: rgba(254, 161, 22, 0.08);
          color: #FEA116;
          opacity: 1;
          transform: translateX(4px);
        }
        .nav-item--active {
          background: rgba(254, 161, 22, 0.15) !important;
          color: #FEA116 !important;
          border: 1px solid rgba(254, 161, 22, 0.4);
          opacity: 1;
        }

        .hk-sidebar-footer {
          padding-top: 15px;
          border-top: 1.5px solid rgba(212, 175, 55, 0.2);
        }
        .hk-btn-logout {
          background: rgba(231, 76, 60, 0.1);
          color: #e74c3c;
          border: 1.5px solid rgba(231, 76, 60, 0.3);
          border-radius: 12px;
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          padding: 10px 0;
          transition: all 0.2s ease;
        }
        .hk-btn-logout:hover {
          background: #e74c3c;
          color: #FAF6F0;
          box-shadow: 0 4px 14px rgba(231, 76, 60, 0.2);
        }

        /* Mobile Toggle Button */
        .hk-mobile-toggle-btn {
          position: fixed;
          top: 75px;
          left: 15px;
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: #0B2447;
          border: 1px solid #FEA116;
          color: #FEA116;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
          cursor: pointer;
        }

        /* Backdrop overlay */
        .hk-sidebar-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 95;
        }

        /* Main Content container */
        .hk-main-content {
          flex-grow: 1;
          background: linear-gradient(135deg, #FAF6F0 0%, #F5EFEB 100%);
          overflow-y: auto;
          overflow-x: hidden;
          position: relative;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .hk-main-content::-webkit-scrollbar {
          display: none;
        }

        .hk-welcome-panel {
          background: linear-gradient(135deg, #0B2447 0%, #050E1A 100%);
          color: #FAF6F0;
          border: 1.5px solid rgba(212, 175, 55, 0.45);
        }

        .hk-gold-tag {
          background: rgba(254, 161, 22, 0.15);
          border: 1px solid #FEA116;
          color: #FEA116;
          font-family: 'Poppins', sans-serif;
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding: 4px 12px;
          border-radius: 99px;
          display: inline-block;
        }

        .hk-welcome-title {
          font-family: 'Cinzel', serif;
          font-weight: 700;
          color: #FEA116;
        }

        .hk-welcome-sub {
          font-family: 'Poppins', sans-serif;
          font-size: 0.9rem;
          opacity: 0.85;
        }

        .hk-countdown-banner {
          background: linear-gradient(90deg, rgba(254, 161, 22, 0.08), rgba(254, 161, 22, 0.18), rgba(254, 161, 22, 0.08));
          border-color: #FEA116 !important;
          color: #0B2447;
          font-family: 'Poppins', sans-serif;
          font-size: 0.95rem;
        }

        .hk-btn-gold {
          background: linear-gradient(135deg, #FEA116 0%, #D4AF37 100%);
          color: #050E1A;
          border: 1.5px solid rgba(255, 255, 255, 0.3);
          font-weight: 700;
        }
        .hk-btn-gold:hover:not(:disabled) {
          background: linear-gradient(135deg, #ff8c00 0%, #fea116 100%);
          color: #050E1A;
          box-shadow: 0 6px 16px rgba(254, 161, 22, 0.3);
        }

        .hk-metric-card {
          background: #ffffff;
          border-radius: 16px;
          border: 1.5px solid rgba(212, 175, 55, 0.25);
          padding: 20px 12px;
          text-align: center;
          box-shadow: 0 8px 24px rgba(11, 29, 55, 0.04);
          transition: all 0.3s ease;
        }
        .hk-metric-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 28px rgba(212, 175, 55, 0.12);
          border-color: #FEA116;
        }
        .hk-metric-card h3 {
          font-family: 'Cinzel', serif;
          font-weight: 800;
          color: #0B2447;
          margin: 6px 0 2px;
          font-size: 1.45rem;
        }
        .hk-metric-card p {
          font-family: 'Poppins', sans-serif;
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #53647C;
          font-weight: 600;
          margin: 0;
        }

        .hk-bookings-section {
          background: #ffffff;
          border: 1.5px solid rgba(212, 175, 55, 0.25);
        }

        .hk-section-header {
          font-family: 'Cinzel', serif;
          font-weight: 700;
          color: #0B2447;
          border-bottom: 2px solid rgba(212, 175, 55, 0.15);
          padding-bottom: 12px;
        }

        .hk-booking-detail-card {
          background: #FAF6F0;
          border-radius: 18px;
          border: 1px solid rgba(212, 175, 55, 0.35);
          padding: 22px;
          box-shadow: 0 6px 20px rgba(11, 29, 55, 0.03);
          font-family: 'Poppins', sans-serif;
          transition: all 0.3s ease;
        }
        .hk-booking-detail-card:hover {
          box-shadow: 0 10px 28px rgba(212, 175, 55, 0.1);
          border-color: #FEA116;
        }

        .hk-room-name {
          font-family: 'Cinzel', serif;
          font-weight: 700;
          color: #0B2447;
          margin: 0;
          font-size: 1.1rem;
        }

        .hk-booking-id {
          font-size: 0.75rem;
          color: #53647C;
          font-weight: 600;
        }

        .hk-card-gold-line {
          width: 100%;
          height: 1px;
          background: rgba(212, 175, 55, 0.25);
        }

        .hk-gold-cost {
          color: #FEA116;
          font-size: 1.05rem;
          font-weight: 700;
        }

        .hk-badge {
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding: 4px 10px;
          border-radius: 99px;
        }
        .hk-badge-pending {
          background: rgba(254, 161, 22, 0.15);
          color: #FEA116;
          border: 1px solid #FEA116;
        }
        .hk-badge-syncing {
          background: rgba(30, 62, 114, 0.1);
          color: #1E3E72;
          border: 1px solid #1E3E72;
        }
        .hk-badge-confirmed {
          background: rgba(46, 204, 113, 0.15);
          color: #2ecc71;
          border: 1px solid #2ecc71;
        }
        .hk-badge-cancelled {
          background: rgba(231, 76, 60, 0.15);
          color: #e74c3c;
          border: 1px solid #e74c3c;
        }
        .hk-badge-completed {
          background: rgba(11, 29, 55, 0.1);
          color: #0B1D37;
          border: 1px solid #0B1D37;
        }

        /* Responsive sidebar styling (mobile-first approach) */
        @media (max-width: 991.98px) {
          .hk-sidebar {
            position: fixed;
            top: 0;
            left: 0;
            height: 100vh;
            transform: translateX(-100%);
            box-shadow: 6px 0 20px rgba(0, 0, 0, 0.15);
          }
          .hk-sidebar--open {
            transform: translateX(0);
          }
          .hk-main-content {
            padding-left: 0;
            padding-top: 60px; /* space for mobile toggle button */
          }
        }

        @media (min-width: 992px) {
          .hk-mobile-toggle-btn {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
}
